import type { Editor } from "@tiptap/react";
import { attachmentPath, uploadAttachment } from "../../../shared/api/attachments";

// Upload plumbing for the document editor: files become universal attachments
// and land in the content as an image node (pictures) or a file link. Only
// capability paths ever enter the content — the server sanitizer rejects
// anything else, and a rejected save would drop the whole update.

export const uploadStateEvent = "impacto:document-upload";

let activeUploads = 0;

function notifyUploadState(editor: Editor) {
  editor.view.dom.dispatchEvent(new CustomEvent(uploadStateEvent, { bubbles: true, detail: activeUploads }));
}

export async function insertUploadedFiles(editor: Editor, files: File[], position: number | null): Promise<void> {
  for (const file of files) {
    activeUploads += 1;
    notifyUploadState(editor);
    try {
      const uploaded = await uploadAttachment(file);
      const path = attachmentPath(uploaded);
      const insertAt = position ?? editor.state.selection.to;
      if (uploaded.contentType.startsWith("image/")) {
        editor
          .chain()
          .focus()
          .insertContentAt(insertAt, { attrs: { alt: uploaded.fileName, src: path }, type: "image" })
          .run();
      } else {
        editor
          .chain()
          .focus()
          .insertContentAt(insertAt, {
            content: [{ marks: [{ attrs: { href: path }, type: "link" }], text: uploaded.fileName, type: "text" }],
            type: "paragraph",
          })
          .run();
      }
      // Follow-up files of the same batch land after the fresh selection.
      position = null;
    } finally {
      activeUploads -= 1;
      notifyUploadState(editor);
    }
  }
}

export function pickAndInsertFiles(editor: Editor, accept: string): void {
  const input = document.createElement("input");
  input.type = "file";
  input.multiple = true;
  if (accept) input.accept = accept;
  input.onchange = () => {
    const files = [...(input.files ?? [])];
    if (files.length > 0) {
      void insertUploadedFiles(editor, files, null);
    }
  };
  input.click();
}
